import { useQuery } from "@tanstack/react-query";
import { Laptop } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DEVICE_SECTION_SHORT_LABELS } from "@/features/zdalny-dostep/device-role-config";
import { badgeBase, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
	type Conversation,
	formatDateTime,
	helpdeskApi,
	useHelpdeskKey,
	useHelpdeskMutation,
} from "./api";
import { DeviceRow } from "./pharmacy-panel";

/**
 * Ręczne parowanie adresu (pracownik biura) z jego urządzeniem biurowym — laptopem
 * albo stanowiskiem IT tej organizacji. Urządzenie nie zna adresu, więc wybiera człowiek;
 * adres dostaje etykietę „Biuro”, gdy jej nie miał.
 */
export function OfficeDeviceButton({
	email,
	deviceUuid,
	size = "sm",
}: {
	email: string;
	deviceUuid: string | null;
	size?: "sm" | "xs";
}) {
	const [open, setOpen] = useState(false);
	const [q, setQ] = useState("");
	const key = useHelpdeskKey();
	const devices = useQuery({
		queryKey: key("office-devices"),
		queryFn: helpdeskApi.officeDevices,
		enabled: open,
		staleTime: 60_000,
	});
	const save = useHelpdeskMutation(
		(uuid: string | null) => helpdeskApi.setContactDevice(email, uuid),
		{
			success: "Zapisano urządzenie adresu",
			error: "Nie udało się zapisać urządzenia",
			onSuccess: () => setOpen(false),
		},
	);
	const needle = q.trim().toLowerCase();
	const shown = (devices.data ?? []).filter((d) =>
		[d.displayName, d.hostname].some((v) => v?.toLowerCase().includes(needle)),
	);

	return (
		<>
			<Button
				size="sm"
				variant="outline"
				className={cn(size === "xs" && "h-6 px-2 text-xs")}
				onClick={() => {
					setQ("");
					setOpen(true);
				}}
			>
				<Laptop className={cn("mr-1.5", size === "xs" ? "size-3" : "size-4")} aria-hidden />
				{deviceUuid ? "Zmień urządzenie" : "Urządzenie biurowe"}
			</Button>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>Urządzenie biurowe adresu</DialogTitle>
						<DialogDescription>
							{email} — laptop biurowy albo stanowisko IT. Adres nie będzie parowany z apteką.
						</DialogDescription>
					</DialogHeader>
					<DialogBody className="space-y-2">
						<Input
							aria-label="Szukaj urządzenia"
							autoFocus
							placeholder="Nazwa urządzenia"
							value={q}
							onChange={(e) => setQ(e.target.value)}
						/>
						{devices.isLoading ? (
							<p className="text-xs text-muted-foreground">Wczytywanie…</p>
						) : devices.isError ? (
							<p className="text-xs text-status-error-text">Nie udało się pobrać urządzeń.</p>
						) : shown.length ? (
							<ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border text-xs">
								{shown.map((d) => (
									<li key={d.uuid}>
										<button
											type="button"
											aria-pressed={d.uuid === deviceUuid}
											disabled={save.isPending}
											onClick={() => save.mutate(d.uuid)}
											className={cn(
												"flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-muted",
												d.uuid === deviceUuid && "bg-primary/10",
											)}
										>
											<span
												className={cn(
													"size-2 shrink-0 rounded-full",
													d.status === "online"
														? "bg-status-success-text"
														: "bg-status-warning-text",
												)}
												title={d.status}
											/>
											<span className="min-w-0 flex-1 truncate">
												{d.displayName ?? d.hostname ?? d.uuid}
											</span>
											<span className={cn(badgeBase, "px-1 py-0", statusClasses.info)}>
												{DEVICE_SECTION_SHORT_LABELS[d.deviceRole] ?? d.deviceRole}
											</span>
											<span className="shrink-0 text-muted-foreground">
												{d.status === "online" ? "online" : formatDateTime(d.lastSeenAt)}
											</span>
										</button>
									</li>
								))}
							</ul>
						) : (
							<p className="text-xs text-muted-foreground">Brak pasujących urządzeń biurowych.</p>
						)}
					</DialogBody>
					<DialogFooter>
						{deviceUuid ? (
							<Button
								variant="outline"
								className="mr-auto"
								disabled={save.isPending}
								onClick={() => save.mutate(null)}
							>
								Odepnij urządzenie
							</Button>
						) : null}
						<Button variant="outline" onClick={() => setOpen(false)}>
							Anuluj
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

/** Urządzenie adresu w panelu czatu: ten sam wiersz z „Połącz” co stanowiska apteki. */
export function OfficeDevice({ device }: { device: NonNullable<Conversation["device"]> }) {
	if ("missing" in device)
		return (
			<p className={cn(badgeBase, "w-fit", statusClasses.warning)}>
				Urządzenie odwołane lub usunięte
			</p>
		);
	return (
		<ul>
			<DeviceRow device={device} />
		</ul>
	);
}
